import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '../../../../components/ui/button';
import { CenteredSpinner } from '../../../../components/ui/Spinner';
import { Input } from '../../../../components/ui/input';
import { StaffListEditor } from '../../../../components/speedToLead/StaffListEditor';
import { MessagesEditor, OutboundWhisperEditor, messagesValid } from '../../../../components/speedToLead/MessagesEditor';
import { validateMessageTemplate } from '../../../../lib/speedToLead/spokenMessages';
import { toStaffList } from '../../../../lib/speedToLead/staffView';
import type { PortalSpeedToLeadView } from '../../../../types/kyckConnect';
import type { StaffRow } from '../../../../types/speedToLead';
import { fetchPortalSpeedToLeadView, savePortalSpeedToLead } from './portalSetupApi';

// THE CLUB'S SPEED TO LEAD STEP (KyckConnect Phase 4): its call list (order and on/off), the spoken messages, how long each phone
// rings, the wait before the second round, and "try again if the lead does not answer". ⚠️ Nothing else: calls on or off, recording,
// the AI-text delay and the call-back number are Kyck's and are not in this form (and not in the server's schema). The numbers are
// held as STRINGS so an emptied box stays empty while it is retyped, and the bounds are the server's, shown here as help text.

function NumberBox({ id, label, suffix, value, onChange, min, max, disabled }: { id: string; label: string; suffix: string; value: string; onChange: (v: string) => void; min: number; max: number; disabled: boolean }) {
  return (
    <label className="block text-sm text-foreground" htmlFor={id}>
      <span className="mb-1 block">{label}</span>
      <span className="flex items-center gap-2">
        <Input id={id} type="number" inputMode="numeric" min={min} max={max} value={value} disabled={disabled} className="w-28" onChange={(e) => onChange(e.target.value)} />
        <span className="text-muted-foreground">{suffix}</span>
      </span>
      <span className="text-xs text-muted-foreground">{min} to {max}</span>
    </label>
  );
}

interface FormState {
  ring: string;
  secondRoundMinutes: string;
  retry: string;
  whisper: string;
  missed: string;
  outbound: string;
}
const formFrom = (v: PortalSpeedToLeadView): FormState => ({
  ring: String(v.staffRingSeconds),
  secondRoundMinutes: String(Math.round(v.roundTwoDelaySeconds / 60)),
  retry: v.retryAfterMinutes === null ? '' : String(v.retryAfterMinutes),
  whisper: v.messages.whisperMessage ?? '',
  missed: v.messages.missedCallMessage ?? '',
  outbound: v.messages.outboundWhisperMessage ?? '',
});

export function PortalSpeedToLeadForm({ accountId, editable }: { accountId: string; editable: boolean }) {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ['portal-kc', 'stl', accountId], queryFn: () => fetchPortalSpeedToLeadView(accountId), retry: false });
  const [form, setForm] = useState<FormState | null>(null);
  const [rows, setRows] = useState<StaffRow[]>([]);
  const [status, setStatus] = useState<string | null>(null);
  useEffect(() => {
    if (!query.data) return;
    setForm(formFrom(query.data));
    setRows(query.data.rows);
  }, [query.data]);

  const save = useMutation({
    mutationFn: () => {
      if (!form || !query.data) throw new Error('Nothing to save.');
      const messagesEditable = !query.data.messages.locked;
      return savePortalSpeedToLead(accountId, {
        staffList: toStaffList(rows),
        staffRingSeconds: Number(form.ring),
        // Held as minutes in the form, as seconds on the wire (the stored field).
        roundTwoDelaySeconds: Math.round(Number(form.secondRoundMinutes) * 60),
        retryAfterMinutes: form.retry.trim() === '' ? null : Number(form.retry),
        ...(messagesEditable
          ? { messages: { whisperMessage: form.whisper.trim() || null, missedCallMessage: form.missed.trim() || null, outboundWhisperMessage: form.outbound.trim() || null } }
          : {}),
      });
    },
    onSuccess: () => {
      setStatus('Saved.');
      void qc.invalidateQueries({ queryKey: ['portal-kc'] });
    },
    onError: (err) => setStatus(err instanceof Error ? err.message : 'Could not save.'),
  });

  if (query.isLoading) return <CenteredSpinner />;
  if (query.error || !query.data || !form) return <p className="text-destructive" role="alert">We could not load your call settings just now.</p>;
  const v = query.data;
  const readOnly = !editable;
  const set = (patch: Partial<FormState>) => {
    setStatus(null);
    setForm({ ...form, ...patch });
  };
  const messagesLocked = v.messages.locked;
  const messagesOk = messagesLocked || (messagesValid(form.whisper, form.missed) && validateMessageTemplate('outbound', form.outbound).ok);
  const minutes = Number(form.secondRoundMinutes);

  return (
    <div className="space-y-5" data-testid="portal-speed-to-lead-form">
      {!v.callsOn && <p className="text-muted-foreground" data-testid="calls-off-note">Calls are off for your club. Talk to Kyck to turn them on. You can still set your call list ready.</p>}

      <section className="space-y-2" aria-label="Who we call">
        <h3 className="kc-label text-muted-foreground">Who we call</h3>
        <p className="text-sm text-muted-foreground">When a new lead comes in, your team is called in this order. Names and numbers come from your business profile.</p>
        <StaffListEditor rows={rows} onChange={(r) => { setStatus(null); setRows(r); }} disabled={readOnly} />
      </section>

      <section className="space-y-3" aria-label="Ring time">
        <h3 className="kc-label text-muted-foreground">How we ring</h3>
        <div className="grid gap-4 sm:grid-cols-3">
          <NumberBox id="stl-ring" label="Ring each person for" suffix="seconds" value={form.ring} min={v.bounds.staffRingSeconds.min} max={v.bounds.staffRingSeconds.max} disabled={readOnly} onChange={(ring) => set({ ring })} />
          <NumberBox id="stl-round2" label="Wait before the second round" suffix="minutes" value={form.secondRoundMinutes} min={v.bounds.roundTwoDelaySeconds.min / 60} max={v.bounds.roundTwoDelaySeconds.max / 60} disabled={readOnly} onChange={(secondRoundMinutes) => set({ secondRoundMinutes })} />
          <NumberBox id="stl-retry" label="Try again if the lead does not answer (leave empty for no retry)" suffix="minutes" value={form.retry} min={v.bounds.retryAfterMinutes.min} max={v.bounds.retryAfterMinutes.max} disabled={readOnly} onChange={(retry) => set({ retry })} />
        </div>
      </section>

      <section className="space-y-3" aria-label="Spoken messages">
        <h3 className="kc-label text-muted-foreground">What is said</h3>
        {messagesLocked && <p className="text-sm text-muted-foreground" data-testid="messages-locked">Kyck manages these messages for your club. To change them, ask your Kyck account manager.</p>}
        <MessagesEditor whisperMessage={form.whisper} missedCallMessage={form.missed} readOnly={readOnly || messagesLocked} onChange={(n) => set({ whisper: n.whisperMessage, missed: n.missedCallMessage })} />
        <OutboundWhisperEditor value={form.outbound} readOnly={readOnly || messagesLocked} onChange={(outbound) => set({ outbound })} />
      </section>

      <p className="text-sm text-muted-foreground">Calls on or off, recording and the number a lead&apos;s call-back rings are managed by Kyck.</p>

      {editable ? (
        <div className="flex items-center gap-3">
          <Button className="kc-save" onClick={() => save.mutate()} disabled={save.isPending || !messagesOk || !Number.isFinite(minutes) || form.ring.trim() === '' || form.secondRoundMinutes.trim() === ''}>
            {save.isPending ? 'Saving…' : 'Save Speed to Lead'}
          </Button>
          {status && <span role="status" className="text-sm text-muted-foreground">{status}</span>}
        </div>
      ) : null}
    </div>
  );
}
